import { db } from '../firebase-config.js';
import { collection, doc, writeBatch } from 'firebase/firestore';
import { isNativeApp } from '../platform.js';
import { APP_VERSION, APP_VERSION_CODE } from '../app-config.js';

function safeStringify(obj) {
  const seen = new WeakSet();
  return JSON.stringify(obj, (k, v) => {
    if (typeof v === 'object' && v !== null) {
      if (seen.has(v)) return '[Circular]';
      seen.add(v);
    }
    return v;
  });
}

let _sessionId = Math.random().toString(36).substring(2, 10) + '-' + Date.now();
let _pendingReports = [];
let _flushTimer = null;
let _uid = null;

// Only send crashes if the user is authenticated (to avoid spam and link to user)
export function setCrashReporterUser(uid) {
  _uid = uid;
}

export async function initCrashReporter() {
  if (isNativeApp) {
    try {
      const { FirebaseCrashlytics } = await import('@capacitor-firebase/crashlytics');
      await FirebaseCrashlytics.setCrashlyticsCollectionEnabled({ enabled: true });
    } catch (err) {
      console.warn('Native Crashlytics not available:', err);
    }
  }

  // Auto-flush timer every 60s
  setInterval(() => flushCrashReports(), 60_000);

  window.addEventListener('beforeunload', () => flushCrashReports());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushCrashReports();
  });
}

export function reportCrash(errorObj) {
  let errPayload;
  if (errorObj instanceof Error) {
    errPayload = {
      name: errorObj.name,
      message: errorObj.message,
      stack: errorObj.stack,
      at: new Date().toISOString()
    };
  } else {
    // Safely extract only primitive fields — spreading arbitrary objects can
    // include non-serializable values (DOM nodes, functions, WeakRefs) that
    // crash safeStringify and silently drop the report.
    const safe = {};
    if (errorObj && typeof errorObj === 'object') {
      for (const [k, v] of Object.entries(errorObj)) {
        if (v == null || typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
          safe[k] = v;
        }
      }
    }
    errPayload = {
      message: errorObj?.msg || errorObj?.message || String(errorObj),
      stack: errorObj?.stack || '',
      ...safe,
      at: new Date().toISOString()
    };
  }

  _pendingReports.push(errPayload);
  
  if (isNativeApp) {
    try {
      import('@capacitor-firebase/crashlytics').then(({ FirebaseCrashlytics }) => {
        FirebaseCrashlytics.recordException({
          message: errPayload.message,
          stacktrace: errPayload.stack || ''
        }).catch(() => {});
      }).catch(() => {});
    } catch (e) {
      // ignore
    }
  }

  if (_pendingReports.length >= 5) {
    flushCrashReports();
  } else if (!_flushTimer) {
    _flushTimer = setTimeout(flushCrashReports, 5000);
  }
}

export async function flushCrashReports() {
  if (_flushTimer) {
    clearTimeout(_flushTimer);
    _flushTimer = null;
  }

  const toSend = [..._pendingReports];
  _pendingReports = []; // clear queue
  
  if (!_uid) return; // Drop guest queue

  try {
    const batch = writeBatch(db);
    const crashesRef = collection(db, 'crash_reports');
    
    // Group them into one document per flush
    const docRef = doc(crashesRef);
    batch.set(docRef, {
      sessionId: _sessionId,
      uid: _uid,
      platform: isNativeApp ? 'android' : 'web',
      appVersion: APP_VERSION,
      versionCode: APP_VERSION_CODE,
      timestamp: new Date().toISOString(),
      errors: JSON.parse(safeStringify(toSend)),
      device: {
        userAgent: navigator.userAgent,
        screen: `${window.innerWidth}x${window.innerHeight}`
      }
    });

    await batch.commit();
  } catch (err) {
    console.warn('[CrashReporter] Failed to send crash report to Firestore', err);
    // Do NOT re-queue payloads if serialization error occurred
  }
}
